import { Loader2 } from 'lucide-react';
import { useAccessControl } from '@/contexts/AccessControlContext';
import { AccessDenied } from '@/components/AccessDenied';
import type { AccessDeniedReason } from '@/services';

interface RequireAccessProps {
  module?: string;
  permission?: string | string[];
  feature?: string;
  children: React.ReactNode;
}

export function RequireAccess({ module, permission, feature, children }: RequireAccessProps) {
  const { isLoading, canAccessModule, hasFeature, hasPermission, hasAnyPermission } = useAccessControl();

  if (isLoading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  let deniedReason: AccessDeniedReason | null = null;

  if (module && !canAccessModule(module)) {
    deniedReason = 'NO_MODULE_ACCESS';
  } else if (feature && !hasFeature(feature)) {
    deniedReason = 'FEATURE_NOT_LICENSED';
  } else if (permission) {
    const permitted = Array.isArray(permission)
      ? hasAnyPermission(permission)
      : hasPermission(permission);
    if (!permitted) deniedReason = 'NO_PERMISSION';
  }

  if (deniedReason) {
    return <AccessDenied reason={deniedReason} />;
  }

  return <>{children}</>;
}
